import * as React from "react";
import { CSSProperties, ReactElement } from "react";
import Dropdown from 'antd/lib/dropdown';
import Tooltip from 'antd/lib/tooltip';
import { defined, isEqual } from "@unifocus/js-utils";
import { Icons } from '@unifocus/react-icons';
import debounce from 'lodash/debounce';
import { ResizeObserver } from '../../../utils/ResizeObserver';
import { HorizontalPanel } from '../../layout/horizontalpanel';
import { Menu, MenuItemProps } from '../menu';
import { Button } from '../button';
import { HorizontalSpacer } from '../../layout/horizontalspacer';

/**
 * {@docCategory ActionBar}
 */
export interface ActionBarItemProps {
   /**
    * Name of the item
    */
   name: string;

   /**
    * Event called on item click
    */
   onClick: () => void;

   /**
    * Icon for item
    */
   icon?: ReactElement;

   /**
    * Show button with icon only
    */
   iconOnly?: boolean;

   /**
    * Tooltip text for button
    */
   tooltip?: string;

   /**
    * Whether item is disabled
    */
   disabled?: boolean;

   /**
    * Size of button
    * @default 'default'
    */
   size?: ButtonSize;
}

export declare type ButtonSize = 'small' | 'middle' | 'large' | undefined;

/**
 * {@docCategory ActionBar}
 */
export interface ActionBarProps {

   /**
    * Array of objects to be shown as buttons or menu items in dropdown
    */
   items: ActionBarItemProps[];

   /**
    * Custom className
    */
   className?: string;

   /**
    * Sets styles on the ActionBar.
    */
   style?: CSSProperties;

   /**
    * Array of objects to be shown only as menu items in dropdown. They go at the end of dropdown list
    * if there is one existing.
    */
   overflowItems?: ActionBarItemProps[];

   /**
    * Limit maximum number of buttons (first buttons to the limit number) to display. Rest goes to overflow.
    * If available space is smaller than specified or default limit, then buttons will be in overflow as regular.
    * @default 3
    */
   maxItemsOutsideOverflow?: number;

   /**
    * To set different size of menu button.
    * @default 'default'
    */
   menuButtonSize?: ButtonSize;

   /**
    * To set different container for popup.
    * @default original holder
    */
   getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;

   /**
    * The width of the action bar. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   width?: string | number;

   /**
    * The minimum width of the action bar. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   minWidth?: string | number;

   /**
    * The maximum width of the action bar. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * Whether to align the buttons that are outside the overflow menu to the left or to the right
    *
    * @defaultvalue right
    */
   menuButtonAlign?: 'right' | 'left';
}

export interface ActionBarState {
   /**
    * actionbar width
    */
   actionbarWidth: number;

   /**
    * Items to be shown as buttons
    */
   visibleItems: ActionBarItemProps[];

   /**
    * Items to be shown as menu items
    */
   overflowItems: ActionBarItemProps[];
}

/**
 * Component receives items which can be rendered as buttons
 * or menu items if they do not fit the container. Optionally it
 * can contain children which will be rendered right of the
 * button group and will always stay outside
 */
export class ActionBar extends React.Component<ActionBarProps, ActionBarState> {
   private _isMounted = true;
   private actionbarRef: HTMLDivElement | null = null;
   private buttonGroup: HTMLElement;
   private buttonsWidths: number[] = [];
   private maxItemsOutsideOverflowDefault = 3;
   private readonly debounceOnWidthChange: any;

   constructor(props: ActionBarProps) {
      super(props);

      this.debounceOnWidthChange = debounce(this.onWidthChange, 50);

      this.state = {
         visibleItems: this.getVisibleItems(this.props.items, 0),
         overflowItems: [],
         actionbarWidth: 0,
      };
   }

   componentDidMount(): void {
      this._isMounted = true;
      if (this.actionbarRef) {
      this.buttonGroup = this.actionbarRef.getElementsByClassName("uf-actionbar-button-group")[0] as HTMLElement;
      this.buttonsWidths = this.getButtonsWidths();

         const lengthOfOverflowItems = this.getLengthOfOverflowItems();

         this.setState({
                          actionbarWidth: this.actionbarRef.offsetWidth,
                          visibleItems: this.getVisibleItems(this.props.items, lengthOfOverflowItems),
                          overflowItems: this.appendCustomOverflowItems(this.getOverflowItems(this.props.items, lengthOfOverflowItems))
                       });
      }
   }

   componentDidUpdate(prevProps: Readonly<ActionBarProps>, prevState: Readonly<ActionBarState>): void {
      if (this.actionbarRef) {
         if (!isEqual(prevProps.items, this.props.items)) {
            this.getNewItems();
         }
         else {
            this.resizeItems(prevProps, prevState);
         }
      }
   }

   componentWillUnmount(): void {
      this._isMounted = false;
   }

   render(): ReactElement {

      const style: CSSProperties = {
         width: convertSize(this.props.width),
         maxWidth: convertSize(this.props.maxWidth) || (this.props.style ? this.props.style.maxWidth : undefined),
         ...this.props.style
      };

      if (this.props.minWidth === undefined) {
         // style.minWidth = style.minWidth;
      }
      else if (defined(this.props.minWidth)) {
         style.minWidth = convertSize(this.props.minWidth);
      }

      return (
         <div className={"uf-actionbar"} style={style}>
            <ResizeObserver
               onWidthChange={(width: number) => {
                  if (this._isMounted) {
                     this.debounceOnWidthChange(width);
                  }
               }}
               style={{ overflow: this.state.visibleItems.length > 0 ? "hidden" : "visible" }}
            >
               <HorizontalPanel
                  nowrap={true}
                  ref={(ref) => this.actionbarRef = ref}
                  className={this.props.className ? `uf-actionbar-container ${this.props.className}` : "uf-actionbar-container"}
               >
                  <HorizontalPanel
                     className={"uf-actionbar-button-group"}
                     align={this.props.menuButtonAlign === 'left' ? 'left' : 'right'}
                     horizontalFill={true}
                     nowrap={true}
                  >
                     {this.getButtons()}
                     {this.state.overflowItems.length > 0 &&
                      <Dropdown
                         overlay={this.getMenuOptions()}
                         overlayClassName={'uf-actionbar-overlay'}
                         className={"uf-actionbar-dropdown"}
                         placement="bottomRight"
                         trigger={["click"]}
                         getPopupContainer={(triggerNode: HTMLElement) => this.props.getPopupContainer &&
                                                                          this.props.getPopupContainer(triggerNode) || document.body}
                      >
                         <Button type="link" className="uf-actionbar-menu-button" size={this.props.menuButtonSize}>
                            <Icons.Ellipsis/>
                         </Button>
                      </Dropdown>}
                  </HorizontalPanel>
                  {this.props.children ?
                   <>
                     <HorizontalSpacer size={'medium'}/>
                     <div className={"uf-actionbar-primary-container"}>{this.props.children}</div>
                   </>
                                       : null}
               </HorizontalPanel>
            </ResizeObserver>
         </div>
      );
   }

   private getButtonsWidths = (): number[] => {
      if (this.actionbarRef) {
         return Array.from(this.actionbarRef.getElementsByClassName('uf-actionbar-button'))
            .map((button: Element) => Math.ceil(button.getBoundingClientRect().width));
      }

      return [];
   };

   private getLengthOfOverflowItems = (): number => {
      const totalNumberOfItems: number = this.props.items.length;
      const maxItemsOutsideOverflow: number = this.props.maxItemsOutsideOverflow !== undefined ? this.props.maxItemsOutsideOverflow
                                                                                               : this.maxItemsOutsideOverflowDefault;
      const overflowItemsNumberLimitedBasedOnMaxItemsOutsideOverflow: number = totalNumberOfItems - maxItemsOutsideOverflow;
      let lengthOfOverflowItems = 0;

      if (this.actionbarRef) {
         let totalWidth: number = this.getTotalWidth();
         const dropdownMenu: HTMLElement = this.actionbarRef
            .getElementsByClassName("uf-actionbar-menu-button")[0] as HTMLElement;
         let widthOfEndButtons = 0;

         if (dropdownMenu) {
            totalWidth += dropdownMenu.offsetWidth;
         }

         if (totalWidth < this.actionbarRef.offsetWidth) {
            return overflowItemsNumberLimitedBasedOnMaxItemsOutsideOverflow;
         }

         for (let i = this.buttonsWidths.length - 1; i >= 0; i--) {
            widthOfEndButtons += this.buttonsWidths[i];
            lengthOfOverflowItems++;

            if ((totalWidth - widthOfEndButtons) <= this.actionbarRef.offsetWidth) {
               break;
            }
         }
      }

      const visibleItemsNumberBelowOrEqualMaxItemsOutsideOverflow: boolean = totalNumberOfItems - lengthOfOverflowItems <= maxItemsOutsideOverflow;

      return visibleItemsNumberBelowOrEqualMaxItemsOutsideOverflow ? lengthOfOverflowItems
                                                                   : overflowItemsNumberLimitedBasedOnMaxItemsOutsideOverflow;
   };

   private getNewItems = () => {
      this.setState({ visibleItems: this.getVisibleItems(this.props.items, 0) },
                    () => {
                       this.buttonsWidths = this.getButtonsWidths();
                       let lengthOfOverflowItems: number = this.getLengthOfOverflowItems();

                       if (lengthOfOverflowItems === 1 && this.getTotalWidth() < this.state.actionbarWidth) {
                          lengthOfOverflowItems--;
                       }

                       this.setState({
                                        visibleItems: this.getVisibleItems(this.props.items, lengthOfOverflowItems),
                                        overflowItems: this.appendCustomOverflowItems(this.getOverflowItems(this.props.items, lengthOfOverflowItems))
                                     });
                    });
   };

   private resizeItems = (prevProps: Readonly<ActionBarProps>, prevState: Readonly<ActionBarState>) => {
      let lengthOfOverflowItems: number = this.getLengthOfOverflowItems();

      if (prevState.actionbarWidth > 0 && prevState.actionbarWidth < this.state.actionbarWidth && lengthOfOverflowItems === 1) {
         lengthOfOverflowItems--;
      }

      const visibleItems: ActionBarItemProps[] = this.getVisibleItems(this.props.items, lengthOfOverflowItems);
      const overflowItems: ActionBarItemProps[] = this.getOverflowItems(this.props.items, lengthOfOverflowItems);

      if (visibleItems.length !== this.state.visibleItems.length) {
         this.setState({
                          visibleItems: visibleItems,
                          overflowItems: this.appendCustomOverflowItems(overflowItems)
                       });
      }
   };

   private getTotalWidth = (): number => {
      const buttonGroupMargins = this.getMargins(this.buttonGroup);
      const buttonGroupBorderWidth = this.getBorderWidth(this.buttonGroup);

      let totalWidth: number = this.getPrimaryContainerWidth() + buttonGroupMargins.marginRight
                               + buttonGroupMargins.marginLeft + buttonGroupBorderWidth;

      this.buttonsWidths.forEach(buttonWidth => {
         totalWidth += buttonWidth;
      });

      return totalWidth;
   };

   private getPrimaryContainerWidth = (): number => {
      let primaryContainerWidth = 0;

      if (this.actionbarRef) {
         const primaryContainer = this.actionbarRef.getElementsByClassName("uf-actionbar-primary-container")[0] as HTMLElement;

         if (primaryContainer) {
            const primaryContainerMargins = this.getMargins(primaryContainer);
            primaryContainerWidth = Math.ceil(primaryContainer.getBoundingClientRect().width + 3)
                                    + primaryContainerMargins.marginLeft + primaryContainerMargins.marginRight;
         }
      }

      return primaryContainerWidth;
   };

   private getBorderWidth = (element: HTMLElement): number => {
      const borderLeftWidth: string = window.getComputedStyle(element)
         .getPropertyValue("border-left-width");
      const borderRightWidth: string = window.getComputedStyle(element)
         .getPropertyValue("border-right-width");

      return (+borderLeftWidth.substring(0, borderLeftWidth.length - 2) +
              +borderRightWidth.substring(0, borderRightWidth.length - 2));
   };

   private getMargins = (element: HTMLElement): { marginRight: number, marginLeft: number } => {
      const marginRight: string = window.getComputedStyle(element)
         .getPropertyValue("margin-right");
      const marginLeft: string = window.getComputedStyle(element)
         .getPropertyValue("margin-left");

      return {
         marginRight: +marginRight.substring(0, marginRight.length - 2),
         marginLeft: +marginLeft.substring(0, marginLeft.length - 2)
      };
   };

   private getButtons = (): ReactElement[] => {
      return this.state.visibleItems.map((item: ActionBarItemProps, i: number) => {
         const button = (<Button
            key={i}
            type="link"
            disabled={item.disabled}
            size={item.size}
            className={"uf-actionbar-button"}
            onClick={() => !item.disabled && item.onClick()}
         >
            {this.getButtonText(item)}
         </Button>);

         if (item.tooltip && !item.disabled) {
            return (<Tooltip
               placement={"left"}
               autoAdjustOverflow={true}
               title={item.tooltip}
               key={i}
               overlayClassName={'uf-actionbar-tooltip'}
            >
               {button}
            </Tooltip>);
         }

         return button;
      });
   };

   private getButtonText = (item: ActionBarItemProps): ReactElement | string => {
      let buttonText: ReactElement | string = item.name;

      if (item.icon) {
         if (item.iconOnly) {
            buttonText = item.icon;
         }
         else {
            buttonText = <>{item.icon} {item.name}</>;
         }
      }

      return buttonText;
   };

   private getMenuOptions = (): ReactElement => {
      const hasIcons: boolean = this.state.overflowItems
         .some((item: ActionBarItemProps) => Object.prototype.hasOwnProperty.call(item,"icon"));

      const menuItems: MenuItemProps[] = this.state.overflowItems
         .map((item: ActionBarItemProps, i: number) => {
            return ({
                  key: ""+i,
                  disabled: item.disabled,
                  onClick: () => !item.disabled && item.onClick(),
                  className: 'uf-actionbar-menu-item',
                  title: item.name,
                  icon: item.icon
               });
         });

      return (<Menu menuItems={menuItems || []}/>);
   };

   private getOverflowItems = (items: ActionBarItemProps[], numberOfItemsInOverflow: number): ActionBarItemProps[] => {
      return numberOfItemsInOverflow > 0 ? items.slice(-numberOfItemsInOverflow) : [];
   };

   private appendCustomOverflowItems = (items: ActionBarItemProps[]) => [...items, ...(this.props.overflowItems || [])];

   private getVisibleItems = (items: ActionBarItemProps[], numberOfItemsInOverflow: number): ActionBarItemProps[] => {
      return items.slice(0, items.length - numberOfItemsInOverflow);
   };

   private onWidthChange = (width: number) => {
      this.setState({ actionbarWidth: width });
   };
}

const convertSize = (size?: string | number) => {
   if (!defined(size)) {
      return undefined;
   }

   if (typeof size === 'string' || size === 0) {
      return size;
   }

   return size + 'px';
};

